/**
 * Billboard: looping background from pulina-movies (`ad_movies/*.mov`), random on each `/` visit (no repeat of the previous clip).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../AuthModule';
import Player from '../Player';
import { getAdvertBackgroundVideoUrl } from './advertVideoConfig';
import AvatarWithDesignerBadge from './AvatarWithDesignerBadge';
import { AvatarFallback } from './profileHelpers';
import {
  dailyQuestionAdvertScaleBucket,
  truncateDailyQuestionForAdvert,
} from '../../config/dailyQuestion';

const API = process.env.REACT_APP_API_URL || '';

const EMPTY_ANSWERS = [];

function uniqueAnswerStrings(answers) {
  const seen = new Set();
  const out = [];
  for (const a of answers) {
    const t = String(a?.answer ?? '').trim();
    if (!t || seen.has(t)) continue;
    seen.add(t);
    out.push(t);
  }
  return out;
}

function normalizeAppPath(p) {
  const raw = p || '/';
  return raw.replace(/^\/+/, '/') || '/';
}

function formatAdvertEventWhen(advert) {
  if (!advert) return '—';
  const d = advert.eventDate && /^\d{4}-\d{2}-\d{2}$/.test(advert.eventDate) ? advert.eventDate : null;
  const t = (advert.time || '').trim() || '';
  const datePart = d ? `${d.slice(8, 10)}.${d.slice(5, 7)}.${d.slice(0, 4)}` : null;
  if (datePart && t) return `${datePart} klo ${t}`;
  if (datePart) return datePart;
  return t || '—';
}

function parseAdvertEventStartMs(advert) {
  if (!advert) return null;
  const d = advert.eventDate && /^\d{4}-\d{2}-\d{2}$/.test(advert.eventDate) ? advert.eventDate : null;
  const tRaw = String(advert.time || '').trim();
  const t = /^\d{1,2}:\d{2}$/.test(tRaw) ? tRaw : null;
  if (!d || !t) return null;
  const isoLocal = `${d}T${t.length === 4 ? `0${t}` : t}:00`;
  const ms = new Date(isoLocal).getTime();
  return Number.isFinite(ms) ? ms : null;
}

function formatCountdownClock(msLeft) {
  const totalSeconds = Math.max(0, Math.floor(msLeft / 1000));
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return {
    days,
    hms: `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`,
  };
}

export default function AdvertSection({ path: routePath }) {
  const { user, openLogin } = useAuth();
  const isAdmin = user?.role === 'admin';
  const isRootAdmin = isAdmin && String(user?.id ?? '').replace(/n$/i, '') === '1';
  const bgVideoRef = useRef(null);
  const prevRoutePathRef = useRef(null);
  const [advertBgVideoUrl, setAdvertBgVideoUrl] = useState(() => getAdvertBackgroundVideoUrl());
  const [advert, setAdvert] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [textAnswer, setTextAnswer] = useState('');
  const [submitError, setSubmitError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [eventPromoAvatarError, setEventPromoAvatarError] = useState(false);
  const [countdownNowMs, setCountdownNowMs] = useState(() => Date.now());

  const loadAdvert = useCallback(() => {
    const q = user?.id != null ? `?userId=${encodeURIComponent(String(user.id))}` : '';
    setLoading(true);
    fetch(`${API}/api/queue/advert${q}`)
      .then((r) => r.json())
      .then((data) => setAdvert(data.advert))
      .catch(() => setAdvert(null))
      .finally(() => setLoading(false));
  }, [user?.id]);

  useEffect(() => {
    loadAdvert();
  }, [loadAdvert]);

  useEffect(() => {
    setEventPromoAvatarError(false);
  }, [advert?.kind, advert?.eventId, advert?.postId, advert?.authorAvatar?.url]);

  const hasDailyQuestion =
    (!advert?.kind || advert.kind === 'dailyQuestion') && Boolean(advert?.question);
  const hasEventPromo = advert?.kind === 'eventPromo' && Boolean(advert?.title);
  const hasPostPromo = advert?.kind === 'postPromo' && Boolean(advert?.title);
  const hasRenderableAdvert = hasDailyQuestion || hasEventPromo || hasPostPromo;
  const showQueueEmptyLoading = !loading && !hasRenderableAdvert;
  const answers = useMemo(
    () => (Array.isArray(advert?.answers) ? advert.answers : EMPTY_ANSWERS),
    [advert],
  );
  const options = Array.isArray(advert?.options) ? advert.options : null;
  const eventPromoStartMs = useMemo(() => parseAdvertEventStartMs(advert), [advert]);
  const eventPromoCountdown = useMemo(() => {
    if (!hasEventPromo || eventPromoStartMs == null) return null;
    const msLeft = eventPromoStartMs - countdownNowMs;
    if (msLeft <= 0) return { live: true, days: 0, hms: null };
    return formatCountdownClock(msLeft);
  }, [hasEventPromo, eventPromoStartMs, countdownNowMs]);
  const uniqueCrowd = useMemo(() => uniqueAnswerStrings(answers), [answers]);
  const optionPickMode = options != null && options.length >= 2;
  // At least two stored answers and two distinct labels — single duplicate is not enough for "clickable list" UX
  const crowdPickMode = answers.length >= 2 && uniqueCrowd.length >= 2;

  const advertQuestionDisplay = useMemo(
    () => (advert?.question != null ? truncateDailyQuestionForAdvert(advert.question) : ''),
    [advert?.question],
  );
  const advertQuestionScaleClass = useMemo(() => {
    const b = dailyQuestionAdvertScaleBucket(advert?.question);
    return b === 0 ? '' : ` Advert-daily-question-text--scale-${b}`;
  }, [advert?.question]);

  useEffect(() => {
    if (!hasDailyQuestion || loading || !advert) return;
    if (process.env.NODE_ENV !== 'development') return;
    // #region agent log (dev-only: never call loopback from production builds)
    const wouldBranch = advert.myAnswer
      ? 'thanks'
      : !user?.id
        ? 'login'
        : optionPickMode
          ? 'options'
          : crowdPickMode
            ? 'crowd'
            : answers.length >= 2
              ? 'text_with_answers'
              : 'text';
    const ingestUrl = 'http://127.0.0.1:7742/ingest/db41cd7b-90e8-4cd8-a293-6db1b5e4382f';
    fetch(ingestUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Debug-Session-Id': 'dd0a7d' },
      body: JSON.stringify({
        sessionId: 'dd0a7d',
        hypothesisId: 'H1',
        location: 'AdvertSection.js:branch-preview',
        message: 'daily advert UI branch (same order as renderModalBody)',
        data: {
          runId: 'post-fix',
          userId: user?.id != null ? String(user.id) : null,
          myAnswerSet: Boolean(advert.myAnswer),
          optionPickMode,
          crowdPickMode,
          optionsLen: options?.length ?? 0,
          uniqueCrowdLen: uniqueCrowd.length,
          answersCount: answers.length,
          wouldBranch,
          firstCrowd: uniqueCrowd.slice(0, 4),
        },
        timestamp: Date.now(),
      }),
    }).catch(() => {});
    // #endregion
  }, [
    hasDailyQuestion,
    loading,
    advert,
    user?.id,
    optionPickMode,
    crowdPickMode,
    options,
    uniqueCrowd,
    answers.length,
  ]);

  useEffect(() => {
    const p = normalizeAppPath(routePath);
    const prev = prevRoutePathRef.current;
    prevRoutePathRef.current = p;
    if (p !== '/') return;
    if (prev != null && prev !== '/') {
      setAdvertBgVideoUrl(getAdvertBackgroundVideoUrl());
    }
  }, [routePath]);

  useEffect(() => {
    const el = bgVideoRef.current;
    if (!el || !advertBgVideoUrl) return;
    el.muted = true;
    el.defaultMuted = true;
    const p = el.play();
    if (p && typeof p.catch === 'function') {
      p.catch(() => {});
    }
  }, [advertBgVideoUrl]);

  useEffect(() => {
    if (!hasEventPromo || eventPromoStartMs == null) return undefined;
    const t = setInterval(() => setCountdownNowMs(Date.now()), 1000);
    return () => clearInterval(t);
  }, [hasEventPromo, eventPromoStartMs]);

  const openTarjous = () => {
    window.location.href = '/tarjous';
  };

  const handleAdvertClick = () => {
    if (isAdmin) return;
    if (loading) return;
    if (hasPostPromo) {
      const href = advert.postUrl || `/forum/post/${encodeURIComponent(advert.postId)}`;
      window.location.assign(href);
      return;
    }
    if (hasEventPromo) {
      const href = advert.eventUrl || `/event/show/${encodeURIComponent(advert.eventId)}`;
      window.location.assign(href);
      return;
    }
    if (hasDailyQuestion) {
      setSubmitError(null);
      setTextAnswer('');
      setModalOpen(true);
      return;
    }
    openTarjous();
  };

  const handleAdvertKeyDown = (e) => {
    if (isAdmin) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleAdvertClick();
    }
  };

  const advertAriaLabel = loading
    ? 'Ladataan mainospalkkia'
    : isAdmin
      ? 'Mainospalkki: esikatselu (ei klikattavissa ylläpitäjälle)'
    : hasPostPromo
      ? `Postausmainos: ${advert.title || 'foorumiviesti'} — siirry keskusteluun`
    : hasEventPromo
      ? `Tapahtumamainos: ${advert.title || 'tapahtuma'} — siirry tapahtuman sivulle`
      : hasDailyQuestion
        ? 'Päivän kysymys: avaa vastauslomake'
        : 'Avaa tarjoustiedot';

  const closeModal = () => {
    setModalOpen(false);
    setSubmitError(null);
  };

  useEffect(() => {
    if (!modalOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setModalOpen(false);
        setSubmitError(null);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [modalOpen]);

  const submitAnswer = (answerStr) => {
    if (!user?.id || advert?.kind !== 'dailyQuestion' || !advert?.dailyQuestionId) return;
    setSubmitting(true);
    setSubmitError(null);
    fetch(`${API}/api/daily-questions/${encodeURIComponent(advert.dailyQuestionId)}/answers`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId: String(user.id), answer: answerStr }),
    })
      .then(async (r) => {
        const data = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(data.error || 'Virhe');
        return data;
      })
      .then(() => {
        loadAdvert();
        closeModal();
      })
      .catch((e) => setSubmitError(e.message || 'Virhe'))
      .finally(() => setSubmitting(false));
  };

  const handleTextSubmit = (e) => {
    e.preventDefault();
    const t = textAnswer.trim();
    if (!t) return;
    submitAnswer(t);
  };

  const renderModalBody = () => {
    if (!advert || advert.kind !== 'dailyQuestion') return null;
    if (advert.myAnswer) {
      return (
        <>
          <p className="DailyQuestionModal-thanks">Kiitos, vastauksesi on tallennettu.</p>
          <div className="Modal-actions">
            <button type="button" className="Button" onClick={closeModal}>
              Sulje
            </button>
          </div>
        </>
      );
    }
    if (!user?.id) {
      return (
        <>
          <p>Kirjaudu sisään vastataksesi.</p>
          <div className="Modal-actions">
            <button type="button" className="Button" onClick={() => { closeModal(); openLogin(); }}>
              Kirjaudu
            </button>
            <button type="button" className="Button Button-secondary" onClick={closeModal}>
              Peruuta
            </button>
          </div>
        </>
      );
    }
    if (optionPickMode) {
      return (
        <>
          <p className="DailyQuestionModal-prompt">Valitse vaihtoehto:</p>
          <ul className="DailyQuestionModal-choices">
            {options.map((label) => (
              <li key={label}>
                <button
                  type="button"
                  className="Button DailyQuestionModal-choice"
                  disabled={submitting}
                  onClick={() => submitAnswer(label)}
                >
                  {label}
                </button>
              </li>
            ))}
          </ul>
        </>
      );
    }
    if (crowdPickMode) {
      return (
        <>
          <p className="DailyQuestionModal-prompt">Valitse vastaus:</p>
          <ul className="DailyQuestionModal-choices">
            {uniqueCrowd.map((label) => (
              <li key={label}>
                <button
                  type="button"
                  className="Button DailyQuestionModal-choice"
                  disabled={submitting}
                  onClick={() => submitAnswer(label)}
                >
                  {label}
                </button>
              </li>
            ))}
          </ul>
        </>
      );
    }
    return (
      <form onSubmit={handleTextSubmit}>
        <label className="DailyQuestionModal-textlabel">
          Vastauksesi
          <textarea
            className="DailyQuestionModal-textarea"
            value={textAnswer}
            onChange={(e) => setTextAnswer(e.target.value)}
            rows={4}
            maxLength={2000}
            disabled={submitting}
          />
        </label>
        <div className="Modal-actions">
          <button type="submit" className="Button" disabled={submitting || !textAnswer.trim()}>
            Lähetä
          </button>
          <button type="button" className="Button Button-secondary" onClick={closeModal} disabled={submitting}>
            Peruuta
          </button>
        </div>
      </form>
    );
  };

  const sectionClass = [
    'Advert-section',
    loading ? 'Advert-section--loading' : '',
    hasDailyQuestion && !loading ? 'Advert-section--daily-question' : '',
    hasEventPromo && !loading ? 'Advert-section--event-promo' : '',
    hasPostPromo && !loading ? 'Advert-section--post-promo' : '',
    advertBgVideoUrl ? 'Advert-section--has-video' : '',
    isAdmin ? 'Advert-section--admin-readonly' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <>
      <div className="Advert-banner-cq">
        <div
          className={sectionClass}
          role={isAdmin ? 'region' : 'button'}
          tabIndex={isAdmin ? undefined : 0}
          aria-label={advertAriaLabel}
          onClick={isAdmin ? undefined : handleAdvertClick}
          onKeyDown={isAdmin ? undefined : handleAdvertKeyDown}
        >
        {advertBgVideoUrl ? (
          <>
            <Player
              ref={bgVideoRef}
              src={advertBgVideoUrl}
              controls={false}
              muted
              autoPlay
              loop
              playsInline
              wrapClassName="Advert-section-bg-player-wrap"
              videoClassName="Advert-section-bg-video"
              ariaHidden
            />
            <div className="Advert-section-bg-shade" aria-hidden />
          </>
        ) : null}
        {loading || showQueueEmptyLoading ? (
          <div className="Advert-loading" aria-busy={loading ? 'true' : undefined} aria-live="polite">
            <img
              src={`${process.env.PUBLIC_URL || ''}/pulina.png`}
              alt=""
              className="Advert-loading-favicon"
              width={32}
              height={32}
            />
            <span className="Advert-loading-text">
              Loading
              <span className="Advert-loading-dots" aria-hidden>
                <span className="Advert-loading-dot">.</span>
                <span className="Advert-loading-dot">.</span>
                <span className="Advert-loading-dot">.</span>
              </span>
            </span>
          </div>
        ) : hasDailyQuestion ? (
          <>
            <div className="Advert-header Advert-header--daily">
              <h5>Päivän kysymys</h5>
              {!advert.myAnswer && !isRootAdmin ? (
                <span className="Advert-daily-pc-badge" title="Vastaa ja ansaitse Pulina-krediittejä">
                  +2 PC
                </span>
              ) : null}
            </div>
            <div className="Advert-content Advert-content--daily">
              <p className={'Advert-daily-question-text' + advertQuestionScaleClass}>{advertQuestionDisplay}</p>
            </div>
            {!advert.myAnswer && !isRootAdmin ? (
              <div className="Advert-footer">
                <span className="Advert-footer-line">Napauta vastataksesi</span>
              </div>
            ) : null}
          </>
        ) : hasPostPromo ? (
          <div className="Advert-event-promo-inner">
            <div className="Advert-header Advert-header--event-promo">
              <h5>Foorumi</h5>
            </div>
            <div className="Advert-content Advert-content--event-promo">
              <div className="Advert-event-promo-host">
                {advert.authorAvatar ? (
                  <div className="Advert-event-promo-avatar-wrap" aria-hidden>
                    {!eventPromoAvatarError && advert.authorAvatar.url ? (
                      <AvatarWithDesignerBadge designer={advert.authorAvatar.avatarDesigner} variant="forum">
                        <img
                          src={advert.authorAvatar.url}
                          alt=""
                          className="Advert-event-promo-avatar-img"
                          onError={() => setEventPromoAvatarError(true)}
                        />
                      </AvatarWithDesignerBadge>
                    ) : (
                      <AvatarWithDesignerBadge designer={advert.authorAvatar.avatarDesigner} variant="forum">
                        <AvatarFallback
                          bubbleColorHex={advert.authorAvatar.bubbleColor}
                          displayName={advert.authorAvatar.displayName || advert.authorDisplayName}
                          username={advert.authorDisplayName}
                          size="small"
                          className="Advert-event-promo-avatar-fallback"
                        />
                      </AvatarWithDesignerBadge>
                    )}
                  </div>
                ) : null}
                <div className="Advert-event-promo-text">
                  <p className="Advert-event-promo-title">{advert.title}</p>
                </div>
              </div>
            </div>
            <div className="Advert-footer">
              <span className="Advert-footer-line">Napauta keskusteluun</span>
            </div>
          </div>
        ) : hasEventPromo ? (
          <div className="Advert-event-promo-inner">
            {advert.enableQA ? (
              <div className="Advert-event-promo-qa-badge" aria-label="Q&A käytössä">
                Q&amp;A
              </div>
            ) : null}
            <div className="Advert-header Advert-header--event-promo">
              <h5>Tapahtuma</h5>
            </div>
            <div className="Advert-content Advert-content--event-promo">
              <div className="Advert-event-promo-host">
                {advert.authorAvatar ? (
                  <div className="Advert-event-promo-avatar-wrap" aria-hidden>
                    {!eventPromoAvatarError && advert.authorAvatar.url ? (
                      <AvatarWithDesignerBadge designer={advert.authorAvatar.avatarDesigner} variant="forum">
                        <img
                          src={advert.authorAvatar.url}
                          alt=""
                          className="Advert-event-promo-avatar-img"
                          onError={() => setEventPromoAvatarError(true)}
                        />
                      </AvatarWithDesignerBadge>
                    ) : (
                      <AvatarWithDesignerBadge designer={advert.authorAvatar.avatarDesigner} variant="forum">
                        <AvatarFallback
                          bubbleColorHex={advert.authorAvatar.bubbleColor}
                          displayName={advert.authorAvatar.displayName || advert.authorDisplayName}
                          username={advert.authorDisplayName}
                          size="small"
                          className="Advert-event-promo-avatar-fallback"
                        />
                      </AvatarWithDesignerBadge>
                    )}
                  </div>
                ) : null}
                <div className="Advert-event-promo-text">
                  <p className="Advert-event-promo-title">{advert.title}</p>
                  <p className="Advert-event-promo-when">{formatAdvertEventWhen(advert)}</p>
                  {advert.durationLabel ? (
                    <p className="Advert-event-promo-duration">Kesto: {advert.durationLabel}</p>
                  ) : null}
                </div>
              </div>
            </div>
            <div className="Advert-footer">
              <span className="Advert-footer-line">Napauta tapahtumasivulle</span>
            </div>
            {eventPromoCountdown ? (
              <div className="Advert-event-promo-countdown" aria-live="polite">
                {eventPromoCountdown.live ? (
                  <span className="Advert-event-promo-countdown-hms">Käynnissä</span>
                ) : (
                  <>
                    {eventPromoCountdown.days > 0 ? (
                      <span className="Advert-event-promo-countdown-days">
                        {eventPromoCountdown.days}pv
                      </span>
                    ) : null}
                    <span className="Advert-event-promo-countdown-hms">
                      {eventPromoCountdown.days > 0 ? `ja ${eventPromoCountdown.hms}` : eventPromoCountdown.hms}
                    </span>
                  </>
                )}
              </div>
            ) : null}
          </div>
        ) : null}
        </div>
      </div>

      {modalOpen && advert && advert.kind === 'dailyQuestion' ? (
        <div className="Modal-backdrop" role="presentation" onClick={closeModal}>
          <div
            className="Modal DailyQuestionModal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="daily-question-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 id="daily-question-modal-title">Päivän kysymys</h2>
            <p className="DailyQuestionModal-question">{advert.question}</p>
            {submitError ? (
              <div className="Modal-error" role="alert">
                {submitError}
              </div>
            ) : null}
            {renderModalBody()}
          </div>
        </div>
      ) : null}
    </>
  );
}
